/* W17-B3 — Governance Analytics : visualisations servies par les données canoniques,
   avec drill-down chart → objets → preuve/provenance. Pas un Power BI bis :
   chaque graphique mène aux objets réels et à l'action. */
import { useEffect, useState } from "react";
import { BarChart3, Loader2, X } from "lucide-react";
import { Link } from "react-router-dom";
import { Bar, BarChart, CartesianGrid, Cell, Line, LineChart, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { api } from "@/lib/api";
import { fmtError, notify } from "@/lib/notify";

const COLORS = ["#7F77DD", "#378ADD", "#1D9E75", "#EF9F27", "#DD5A5A", "#8899AA"];
const heatColor = (score) => score >= 16 ? "#DD5A5A" : score >= 9 ? "#EF9F27" : score >= 4 ? "#C9B33B" : "#1D9E75";

export const GovAnalytics = () => {
  const [data, setData] = useState(null);
  const [drill, setDrill] = useState(null);
  const [drillItems, setDrillItems] = useState(null);

  useEffect(() => {
    api.get("/workspace/gov-analytics").then((r) => setData(r.data))
      .catch((e) => notify.error(fmtError(e)));
  }, []);

  const openDrill = async (title, ids, collection = "workspace_risks") => {
    if (!ids?.length) return;
    setDrill({ title, collection });
    setDrillItems(null);
    try {
      const r = await api.get(`/workspace/gov-analytics/drill?ids=${ids.slice(0, 50).join(",")}&collection=${collection}`);
      setDrillItems(r.data.items);
    } catch (e) { notify.error(fmtError(e)); setDrill(null); }
  };

  if (!data) return <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#7F77DD]" /></div>;
  const k = data.kpis;
  const kpiCards = [
    ["Risques", k.risks_total], ["Sévérité nette moy.", k.net_severity_avg ?? "—"],
    ["Risques critiques", k.critical_risks], ["Incidents ouverts", k.incidents_open],
    ["Contrôles", k.controls_total], ["Obligations ouvertes", k.obligations_open],
  ];
  const grid = {};
  (data.risk_matrix || []).forEach((c) => { grid[`${c.impact}x${c.probability}`] = c; });

  return (
    <div className="space-y-4" data-testid="gov-analytics">
      <div className="text-[10px] px-2.5 py-1.5 rounded border border-white/10 text-zinc-400 bg-white/[0.02]">
        {data.note}
      </div>
      <div className="grid grid-cols-3 md:grid-cols-6 gap-2" data-testid="gov-analytics-kpis">
        {kpiCards.map(([l, v]) => (
          <div key={l} className="noam-card p-3 text-center">
            <div className="text-xl font-semibold text-white">{v}</div>
            <div className="text-[9px] uppercase tracking-wider text-zinc-500 mt-0.5">{l}</div>
          </div>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        {/* Heatmap nette 5×5 — drill-down par cellule */}
        <div className="noam-card p-4" data-testid="gov-analytics-heatmap">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-1.5">
            <BarChart3 className="w-3.5 h-3.5 text-[#7F77DD]" /> Matrice des risques (net) — cliquer une cellule
          </div>
          <div className="grid grid-cols-6 gap-1 text-[9px]">
            <div />
            {[1, 2, 3, 4, 5].map((p) => <div key={p} className="text-center text-zinc-500">P{p}</div>)}
            {[5, 4, 3, 2, 1].map((i) => (
              <>
                <div key={`i${i}`} className="text-zinc-500 flex items-center">I{i}</div>
                {[1, 2, 3, 4, 5].map((p) => {
                  const c = grid[`${i}x${p}`];
                  return (
                    <button key={`${i}${p}`} disabled={!c}
                      onClick={() => c && openDrill(`Risques I${i} × P${p}`, c.ids)}
                      data-testid={`gov-heat-${i}-${p}`}
                      className="h-9 rounded flex items-center justify-center font-semibold text-black/80 disabled:opacity-100 transition-transform hover:scale-105"
                      style={{ background: c ? heatColor(i * p) : "rgba(255,255,255,0.03)" }}>
                      {c?.count || ""}
                    </button>
                  );
                })}
              </>
            ))}
          </div>
        </div>

        {/* Catégories — barres */}
        <div className="noam-card p-4" data-testid="gov-analytics-categories">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Risques par catégorie — cliquer une barre</div>
          <ResponsiveContainer width="100%" height={210}>
            <BarChart data={data.risks_by_category} onClick={(e) => {
              const p = e?.activePayload?.[0]?.payload;
              if (p) openDrill(`Catégorie ${p.key}`, p.ids);
            }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" />
              <XAxis dataKey="key" tick={{ fill: "#8899AA", fontSize: 9 }} />
              <YAxis tick={{ fill: "#8899AA", fontSize: 9 }} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "#0b0f18", border: "1px solid #ffffff20", fontSize: 11 }} />
              <Bar dataKey="count" cursor="pointer">
                {data.risks_by_category.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Incidents — camembert */}
        <div className="noam-card p-4" data-testid="gov-analytics-incidents">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Incidents par sévérité</div>
          <ResponsiveContainer width="100%" height={210}>
            <PieChart>
              <Pie data={data.incidents_by_severity} dataKey="count" nameKey="key" innerRadius={45} outerRadius={80}
                onClick={(p) => openDrill(`Incidents ${p.key}`, p.ids, "workspace_incidents")} cursor="pointer"
                label={({ key, count }) => `${key} (${count})`} labelLine={false} fontSize={10}>
                {data.incidents_by_severity.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip contentStyle={{ background: "#0b0f18", border: "1px solid #ffffff20", fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Tendance — courbe */}
        <div className="noam-card p-4" data-testid="gov-analytics-trend">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Nouveaux risques par mois</div>
          <ResponsiveContainer width="100%" height={210}>
            <LineChart data={data.risks_trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" />
              <XAxis dataKey="month" tick={{ fill: "#8899AA", fontSize: 9 }} />
              <YAxis tick={{ fill: "#8899AA", fontSize: 9 }} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "#0b0f18", border: "1px solid #ffffff20", fontSize: 11 }} />
              <Line type="monotone" dataKey="count" stroke="#7F77DD" strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Top risques — table drill */}
      <div className="noam-card p-4" data-testid="gov-analytics-top-risks">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Top risques (score net) — cliquer pour la preuve</div>
        {(data.top_risks || []).map((r) => (
          <button key={r.id} onClick={() => openDrill(r.title, [r.id])}
            className="w-full flex items-center gap-2 py-1.5 border-b border-white/5 text-left hover:bg-white/[0.03] px-1 rounded">
            <span className="w-8 text-center text-[11px] font-semibold rounded" style={{ color: heatColor(r.net_score) }}>{r.net_score}</span>
            <span className="text-xs text-zinc-200 flex-1 truncate">{r.title}</span>
            <span className="text-[9px] text-zinc-500">{r.categorie}</span>
            <span className="text-[9px] text-zinc-500 w-28 truncate">{r.owner}</span>
          </button>
        ))}
      </div>

      {/* Panneau drill-down : objets → preuve/provenance */}
      {drill && (
        <div className="fixed inset-y-0 right-0 w-[400px] z-50 noam-card rounded-none border-l border-white/10 flex flex-col" data-testid="gov-analytics-drill-panel">
          <div className="p-3 border-b border-white/10 flex items-center gap-2">
            <div className="text-sm font-semibold text-white flex-1 truncate">{drill.title}</div>
            <button onClick={() => setDrill(null)} data-testid="gov-drill-close" className="text-zinc-500 hover:text-white"><X className="w-4 h-4" /></button>
          </div>
          <div className="flex-1 overflow-y-auto p-3 space-y-2">
            {!drillItems && <Loader2 className="w-5 h-5 animate-spin text-[#7F77DD] mx-auto mt-6" />}
            {(drillItems || []).map((it) => (
              <div key={it.id} className="rounded border border-white/5 bg-white/[0.02] p-2.5 space-y-1">
                <div className="text-xs text-white">{it.title}</div>
                <div className="text-[9px] text-zinc-500 flex gap-2 flex-wrap">
                  {it.categorie && <span>{it.categorie}</span>}
                  {it.impact_net != null && <span>I{it.impact_net}×P{it.probabilite_nette} net</span>}
                  {it.severity && <span>sévérité {it.severity}</span>}
                  {(it.status || it.statut) && <span>{it.status || it.statut}</span>}
                  {it.owner && <span>{it.owner}</span>}
                </div>
                {(it.provenance || it.imported_from) && (
                  <div className="text-[9px] text-emerald-400/80">
                    Preuve : {it.provenance?.excerpt ? `« ${String(it.provenance.excerpt).slice(0, 120)}… »` : it.provenance?.document_name || it.provenance?.source_label || `importé de ${it.imported_from}`}
                  </div>
                )}
                <Link to={drill.collection === "workspace_incidents" ? "/app/gouvernance" : "/app/risques"}
                  className="text-[9px] text-[#7F77DD] hover:underline">Ouvrir dans le registre →</Link>
              </div>
            ))}
            {drillItems && drillItems.length === 0 && <div className="text-[11px] text-zinc-500 text-center mt-6">Aucun objet.</div>}
          </div>
        </div>
      )}
    </div>
  );
};

export default GovAnalytics;
